import React from 'react';
import { useSelector } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Button from 'yoda-core-components/lib/components/Button/Button';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Marketplace from 'yoda-core-components/lib/assets/icons/utility/Marketplace.svg';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import ErrorBoundary from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import ProductConstants from '../../helpers/ProductConstants';

const SellerInformationContent = ({ sellerData, onClose }) => {
    const { sellerName, sellerAddress, sellerPhone, sellerEmail } = sellerData;
    const hasAddress =
        sellerAddress &&
        (sellerAddress.street || sellerAddress.city || sellerAddress.state || sellerAddress.zip);
    // Trim to guard against whitespace-only strings from backend
    const validPhone = sellerPhone && sellerPhone.trim();
    const validEmail = sellerEmail && sellerEmail.trim();

    return (
        <div data-automation-id="seller-information-content">
            <div className={dt(['flex', 'items-center'])} data-automation-id="seller-info-block">
                <span
                    className={dt([
                        'w-6',
                        'h-6',
                        'shrink-0',
                        'inline-block',
                        'text-center',
                        'mr-2',
                    ])}
                    data-automation-id="seller-info-icon"
                >
                    <Icon iconType="svg" width="24px" height="24px" viewBox="0 0 18 16">
                        <Marketplace />
                    </Icon>
                </span>
                <span
                    className={dt([
                        'text-base',
                        'font-open-sans-semibold',
                        'text-black',
                        'break-words',
                        'min-w-0',
                    ])}
                    data-automation-id="seller-info-name"
                >
                    {sellerName}
                </span>
            </div>
            <div className={dt(['ml-8', 'text-base', 'text-black'])}>
                {hasAddress && (
                    <div
                        className={dt(['pt-2', 'break-words'])}
                        data-automation-id="seller-info-address"
                    >
                        {sellerAddress.street && <div>{sellerAddress.street}</div>}
                        {(sellerAddress.city || sellerAddress.state || sellerAddress.zip) && (
                            <div>
                                {[sellerAddress.city, sellerAddress.state]
                                    .filter(Boolean)
                                    .join(', ')}
                                {sellerAddress.zip ? ` ${sellerAddress.zip}` : ''}
                            </div>
                        )}
                    </div>
                )}
                {validPhone && (
                    <div className={dt(['pt-2'])} data-automation-id="seller-info-phone">
                        <a
                            href={`tel:${validPhone}`}
                            className={dt(['text-black', 'underline'])}
                            aria-label={`Call seller at ${validPhone}`}
                        >
                            {validPhone}
                        </a>
                    </div>
                )}
                {validEmail && (
                    <div className={dt(['pt-2'])} data-automation-id="seller-info-email">
                        <a
                            href={`mailto:${validEmail}`}
                            className={dt(['text-black', 'underline', 'break-all'])}
                            aria-label={`Email seller at ${validEmail}`}
                        >
                            {validEmail}
                        </a>
                    </div>
                )}
                <p
                    className={dt(['text-base', 'mt-4', 'mb-0'])}
                    data-automation-id="seller-reporting-copy"
                >
                    {ProductConstants.SELLER_REPORTING_COPY.before}
                    <span className={dt(['font-open-sans-semibold'])}>
                        {ProductConstants.SELLER_REPORTING_COPY.phone}
                    </span>
                    {ProductConstants.SELLER_REPORTING_COPY.after}
                </p>
            </div>
            {onClose && (
                <div className={dt(['mt-6', 'text-center'])}>
                    <Button
                        buttonType="Secondary"
                        onClick={onClose}
                        automationId="seller-details-ok-btn"
                        className={dt(['!px-2', '!py-2'])}
                    >
                        OK
                    </Button>
                </div>
            )}
        </div>
    );
};

const SellerInformation = ({ showHeading = true }) => {
    const featureFlags = useSelector((state) => selectFeatureFlags(state));
    const marketPlaceInfo = useSelector((state) => selectMarketPlaceSellerInfo(false)(state)) || {};

    const isMarketplace = featureFlags.enableMarketPlace && marketPlaceInfo.isMarketPlaceProduct;

    if (!isMarketplace) {
        return null;
    }

    const sellerData = {
        sellerName: marketPlaceInfo.sellerName || null,
        sellerAddress: marketPlaceInfo.sellerAddress || null,
        sellerPhone: marketPlaceInfo.sellerPhone || null,
        sellerEmail: marketPlaceInfo.sellerEmail || null,
    };

    if (!sellerData.sellerName) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div data-automation-id="seller-information-section">
                {showHeading && (
                    <div
                        className={dt([
                            'font-open-sans-semibold',
                            'text-xlarge',
                            'text-black',
                            'pb-3',
                        ])}
                    >
                        {ProductConstants.SELLER_INFORMATION_HEADING}
                    </div>
                )}
                <SellerInformationContent sellerData={sellerData} />
            </div>
        </ErrorBoundary>
    );
};

export { SellerInformationContent };
export default SellerInformation;
